import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import {
  getCustomerUserId,
  isLoggedInCustomer,
  loadSavedQuestions,
  getSavedQuestionDisplayStatus,
} from '../utils/customerAccount';
import { getPublicLandscapingQuestion } from '../services/landscapingQuestionsApi';

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export default function MyQuestionsPage() {
  const navigate = useNavigate();
  const userId = getCustomerUserId();
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loadingLive, setLoadingLive] = useState(true);

  const currentUserId = userId;
  const stored = useMemo(() => (userId ? loadSavedQuestions(userId) : []), [userId]);

  useEffect(() => {
    document.title = 'My questions | PlantaDay';
  }, []);

  useEffect(() => {
    if (!isLoggedInCustomer()) {
      navigate('/login', { replace: true, state: { returnTo: '/dashboard/my-questions' } });
    }
  }, [navigate]);

  useEffect(() => {
    if (!userId || stored.length === 0) {
      setRows(stored);
      setLoadingLive(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingLive(true);
      const slice = stored.slice(0, 40);
      const enriched = await Promise.all(
        slice.map(async (item) => {
          try {
            const live = await getPublicLandscapingQuestion(item.id);
            return {
              ...item,
              question_text: live.question_text || item.question_text,
              ai_answer: live.ai_answer || item.ai_answer,
              created_at: live.created_at || item.created_at,
              expert_notes: live.expert_notes || item.expert_notes || [],
            };
          } catch {
            return item;
          }
        })
      );
      if (!cancelled) {
        setRows(enriched);
        setLoadingLive(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, stored]);

  if (!isLoggedInCustomer()) {
    return null;
  }

  const toggleExpand = (id) => {
    setExpanded((e) => ({ ...e, [id]: !e[id] }));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNav currentUserId={currentUserId} />
      <main className="flex-1 max-w-3xl mx-auto px-4 py-10 sm:py-14 w-full">
        <h1 className="text-3xl font-bold text-gray-900">My questions</h1>
        <p className="mt-2 text-gray-600 leading-relaxed">
          Your saved yard and plant questions. We will let you know when John or a designer adds a note.
        </p>

        {loadingLive && rows.length > 0 && (
          <p className="mt-4 text-sm text-gray-500">Updating status.</p>
        )}

        {rows.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
            <p className="text-gray-700">You have not asked anything yet.</p>
            <Link
              to="/ask-ai"
              className="mt-6 inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700"
            >
              Ask your first question
            </Link>
          </div>
        ) : (
          <ul className="mt-8 space-y-4">
            {rows.map((q) => {
              const status = getSavedQuestionDisplayStatus(q.expert_notes);
              const isOpen = !!expanded[q.id];
              return (
                <li
                  key={q.id}
                  className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
                >
                  <p className="text-gray-900 font-medium line-clamp-2 whitespace-pre-wrap">{q.question_text}</p>
                  <p className="mt-2 text-xs text-gray-500">{formatDate(q.created_at)}</p>
                  <div className="mt-3">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${status.className}`}>
                      {status.pill}
                    </span>
                  </div>
                  <div className="mt-4 text-sm text-gray-700">
                    <p className={`whitespace-pre-wrap leading-relaxed ${isOpen ? '' : 'line-clamp-2'}`}>
                      {q.ai_answer}
                    </p>
                    <button
                      type="button"
                      onClick={() => toggleExpand(q.id)}
                      className="mt-2 text-sm font-semibold text-green-700 hover:underline"
                    >
                      {isOpen ? 'Show less' : 'Read full answer'}
                    </button>
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <Link
                      to={`/ask-ai/${q.id}`}
                      className="text-sm font-semibold text-blue-700 hover:underline"
                    >
                      Open full question and answer
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
      <Footer />
    </div>
  );
}
